/**
 * Helpers to convert a review template model into a service-contract.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get convertTemplateModelToServiceContract () {
        return convertTemplateModelToServiceContract;
    },
    get withParticipantNames () {
        return withParticipantNames;
    }
});
const _models = require("@performyard/models");
const convertTemplateModelToServiceContract = (input, includeFormAssignments = true)=>{
    const forms = input.forms;
    const template = {
        _id: input._id,
        companyId: input.company,
        name: input.name,
        formAssignments: [],
        formAssignmentsCount: input.formAssignmentsCount ?? 0,
        createdAt: input.createdAt,
        createdBy: input.createdBy,
        updatedAt: input.lastUpdate,
        updatedBy: input.updatedBy,
        lastOpenedAt: input.lastOpenedAt,
        lastOpenedBy: input.lastOpenedBy
    };
    if (includeFormAssignments && forms) {
        template.formAssignments = forms.map((form)=>convertFormAssignment(form));
    }
    return template;
};
const withParticipantNames = (input, template)=>{
    const userNamesById = Object.fromEntries((input.participantUsers ?? []).map((u)=>[
            u.id,
            u.name
        ]));
    const formNamesByAssignmentId = Object.fromEntries((input.forms ?? []).map((f)=>[
            String(f.id),
            f.formName
        ]));
    return {
        ...template,
        formAssignments: template.formAssignments.map((fa)=>addNamesToFormAssignment(fa, formNamesByAssignmentId[String(fa.id)], userNamesById))
    };
};
// ============================================================================
// Form Assignment Converter (core)
// ============================================================================
const convertFormAssignment = (input)=>{
    const base = buildBase(input);
    if (input.external) {
        return {
            ...base,
            external: true,
            authors: getExternalAuthors(input)
        };
    }
    if (input.easyAdd) {
        return {
            ...base,
            easyAdd: true,
            daysToCompleteAuthors: input.daysToCompleteAuthors,
            requiresApproval: input.requiresApproval ?? null,
            subjectEmployeeChooseAuthor: getSubjectEmployeeChooseAuthor(input),
            whoCanEasyAdd: getWhoCanEasyAdd(input),
            ...input.allowComments != null && {
                allowComments: input.allowComments
            },
            ...input.isActive != null && {
                isActive: input.isActive
            },
            ...input.signers && {
                signers: input.signers,
                daysToCompleteSigners: input.daysToCompleteSigners
            }
        };
    }
    return {
        ...base,
        basic: true,
        authors: getInternalAuthors(input),
        daysToCompleteAuthors: input.daysToCompleteAuthors,
        ...input.signers && {
            signers: input.signers,
            daysToCompleteSigners: input.daysToCompleteSigners
        }
    };
};
const buildBase = (input)=>{
    // Legacy easy-add documents store visibility under `visibility` instead of `visible`.
    const legacyVisibility = input.visibility;
    return {
        id: input.id,
        formBaseId: input.formBaseId ?? input.formId,
        visible: input.visible ?? legacyVisibility ?? false,
        redacted: input.redacted ?? false
    };
};
/**
 * Old documents may have a singular deprecated `author` field instead of (or alongside) `authors`.
 * Merge and dedupe by string key (relative-role literal or stringified ObjectId).
 */ const getInternalAuthors = (input)=>{
    const legacy = "author" in input && input.author && !(typeof input.author === "object" && "email" in input.author) ? [
        input.author
    ] : [];
    const fromArray = "authors" in input && input.authors ? input.authors : [];
    const all = [
        ...legacy,
        ...fromArray
    ];
    const seen = new Set();
    return all.filter((a)=>{
        const key = typeof a === "string" ? a : a.toString();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};
// ============================================================================
// Form Assignment — name layering
// ============================================================================
const isExternal = (fa)=>"external" in fa && fa.external === true;
const isEasyAdd = (fa)=>"easyAdd" in fa && fa.easyAdd === true;
const addNamesToFormAssignment = (fa, formName, userNamesById)=>{
    if (isExternal(fa)) {
        return {
            ...fa,
            formName
        };
    }
    if (isEasyAdd(fa)) {
        return {
            ...fa,
            formName,
            ...fa.signers && {
                signers: convertNamedParticipants(fa.signers, userNamesById)
            }
        };
    }
    const internal = fa;
    return {
        ...internal,
        formName,
        authors: convertNamedParticipants(internal.authors, userNamesById),
        ...internal.signers && {
            signers: convertNamedParticipants(internal.signers, userNamesById)
        }
    };
};
// ============================================================================
// Field Helpers
// ============================================================================
/**
 * Convert external author_objects to the API authors format.
 * Filters out null/undefined values from each author.
 * Old documents may have a singular deprecated `author` field instead of (or alongside)
 * `author_objects` — preserve both.
 */ const getExternalAuthors = (input)=>{
    const fromObjects = "authorObjects" in input && input.authorObjects ? input.authorObjects : [];
    const legacyAuthor = "author" in input && input.author && typeof input.author === "object" && "email" in input.author ? [
        input.author
    ] : [];
    const all = [
        ...legacyAuthor,
        ...fromObjects
    ];
    const seen = new Set();
    const deduped = all.filter((a)=>{
        if (!a.email) return true;
        if (seen.has(a.email)) return false;
        seen.add(a.email);
        return true;
    });
    return deduped.map((author)=>{
        // Lodash camelCase converts "email-body" → "emailBody" at runtime,
        // but CamelCaseKeys only handles underscores, so we cast for this field.
        const emailBody = author.emailBody;
        return Object.fromEntries(Object.entries({
            name: author.name,
            email: author.email,
            emailBody
        }).filter(([, v])=>v != null));
    });
};
/**
 * Resolve whoCanEasyAdd from the new field or legacy who_can_add object.
 * Defaults to "any-employee" when neither is set, matching legacy template behavior.
 */ const getWhoCanEasyAdd = (input)=>{
    if ("whoCanEasyAdd" in input && input.whoCanEasyAdd) return input.whoCanEasyAdd;
    if ("whoCanAdd" in input && input.whoCanAdd && typeof input.whoCanAdd === "object") {
        const whoCanAddObj = input.whoCanAdd;
        if (whoCanAddObj.subject) return "subject";
        if (whoCanAddObj.managerOrChainOfCommand) return "manager-chain-of-command";
        if (whoCanAddObj.adminOrGroupAdmin) return "admin";
        if (whoCanAddObj.anyEmployee) return "any-employee";
    }
    return "any-employee";
};
/**
 * Resolve subjectEmployeeChooseAuthor, mapping legacy DB values onto the new enum.
 * Legacy easy-add documents store this as "", "Self", or "AnyEmployee".
 */ const getSubjectEmployeeChooseAuthor = (input)=>{
    const value = input.subjectEmployeeChooseAuthor;
    if (value === "self" || value === "Self") return "self";
    if (value === "any-employee" || value === "AnyEmployee") return "any-employee";
    return "any-employee";
};
/**
 * Resolve a participant array to `{id, name}` objects.
 * - String literals (e.g. "self", "manager") get their canonical labels.
 * - ObjectId instances are stringified and looked up in `userNamesById`.
 */ const convertNamedParticipants = (participants, userNamesById)=>participants.map((p)=>{
        if (typeof p === "string") {
            return {
                id: p,
                name: _models.RELATIVE_PARTICIPANT_LABELS[p] ?? ""
            };
        }
        const id = p.toString();
        return {
            id,
            name: userNamesById[id] ?? ""
        };
    });
